import * as React from 'react';
import dayjs from 'dayjs';
import { IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import { makeStyles } from '@mui/styles';
import { colors } from '../../theme';
import { Table } from '../shared/table/table';
import { SupplierTableType } from '../../types/supplier.types';
import { ToolTip } from '../shared/tooltip/tooltip';

const color = colors();

type SupplierActionType = {
	onEditClickCallback: Function;
	onEmailClickHref: string;
};
const SupplierActionItems = ({ onEditClickCallback, onEmailClickHref }: SupplierActionType) => {
	const classes = useStyles();

	return (
		<div className={classes.iconButton} id="action-buttons">
			<ToolTip
				tooltipText="Edit"
				children={
					<IconButton id="edit_row" size="small" onClick={() => onEditClickCallback()}>
						<EditIcon fontSize="inherit" />
					</IconButton>
				}
			/>
			<ToolTip
				tooltipText="Send Email"
				children={
					<IconButton id="email_row" size="small" href={onEmailClickHref}>
						<EmailIcon fontSize="inherit" />
					</IconButton>
				}
			/>
		</div>
	);
};

export const SupplierTable = ({
	data,
	totalCount,
	page,
	onRowSelectedCallback,
	onCellClickCallback,
	onEditClickCallback,
	onEmailClickHref,
	onPageChange,
	rowsPerPage,
	onRowsPerPageChange,
}: SupplierTableType) => {
	const classes = useStyles();
	const isAdmin: boolean = localStorage.getItem('roles')?.includes('TT.Admin') || false;
	const SupplierColumns: GridColDef[] = [
		{
			field: 'supplierName',
			headerName: 'Supplier Name',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 250,
			flex: 1,
		},
		{
			field: 'platform',
			headerName: 'Platform',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 110,
			flex: 1,
			valueFormatter: ({ value }) => `${value?.tpName} - ${value?.scopeStatus}`,
			renderCell: (params) => {
				const platformStatus =
					params.row?.platform?.scopeStatusId === 1
						? 'Onboarded'
						: params.row?.platform?.scopeStatusId === 2
						? 'In scope - In progress'
						: 'In scope - won’t onboard';

				return (
					<>
						{params.row?.platform && params.row?.platform.tpId !== 0 && (
							<div title={`${params.row?.platform?.tpName} - ${platformStatus}`} className={classes.platformCell}>
								<img
									src={require(`../../images/${params.row?.platform?.tpName?.toLocaleLowerCase().replaceAll(' ', '')}${
										params.row?.platform?.scopeStatusId === 1
											? '_reg'
											: params.row?.platform?.scopeStatusId === 4
											? '_inv'
											: ''
									}.png`)}
									alt={params.row?.platform?.tpName}
									width="24px"
									height="24px"
								/>
								{platformStatus}
							</div>
						)}
					</>
				);
			},
		},
		{
			field: 'scopeStartDate',
			headerName: 'Scope Start Date',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 100,
			flex: 1,
			valueGetter: (params) =>
				params.row?.platform?.scopeStartDate && dayjs(params.row?.platform?.scopeStartDate).format('DD MMM YYYY'),
		},
		{
			field: 'activationDate',
			headerName: 'Activation Date',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 100,
			flex: 1,
			valueGetter: (params) => params.row?.activationDate && dayjs(params.row?.activationDate).format('DD MMM YYYY'),
		},
		{
			field: 'supplierId',
			headerName: 'SPUR ID',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 80,
			flex: 1,
		},
		{
			field: 'supplierCode',
			headerName: 'Supplier Code',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 80,
			flex: 1,
		},
		{
			field: 'supplierStatus',
			headerName: 'SPUR Status',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 80,
			flex: 1,
		},
		{
			field: 'supplierTypes',
			headerName: 'Supplier Type',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 135,
			flex: 1,
		},
		{
			field: 'supplierCategory',
			headerName: 'Category',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 90,
			flex: 1,
		},
		{
			field: 'countryName',
			headerName: 'Country',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 80,
			flex: 1,
		},
		{
			field: 'bouncedOrderCount',
			headerName: 'TG Bounced Orders',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 80,
			flex: 1,
		},
		{
			field: 'comments',
			headerName: 'Comments',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 110,
			flex: 1,
			valueGetter: (params) => params.row?.platform?.comments,
		},
		{
			field: 'action',
			headerName: '',
			hideSortIcons: true,
			sortable: false,
			disableColumnMenu: true,
			minWidth: 10,
			flex: 1,
			align: 'right',
			renderCell: () =>
				SupplierActionItems({
					onEditClickCallback: onEditClickCallback,
					onEmailClickHref: onEmailClickHref,
				}),
		},
	];

	return (
		<Table
			width="calc(100% - 100px)"
			height="calc(100% - 260px)"
			columns={isAdmin ? SupplierColumns : SupplierColumns.slice(0, -1)}
			data={data}
			totalCount={totalCount || data.length}
			page={page}
			pageSize={rowsPerPage}
			pageSizeOptions={[10, 25, 50, 100]}
			checkbox
			onRowSelectedCallback={onRowSelectedCallback}
			onCellClickCallback={onCellClickCallback}
			onPageChange={onPageChange}
			onRowsPerPageChange={onRowsPerPageChange}
		></Table>
	);
};

const useStyles = makeStyles(() => ({
	iconButton: {
		paddingRight: '5px',
		visibility: 'hidden',
		'& .MuiIconButton-root': {
			color: color.signal.info,
		},
	},
	platformCell: {
		display: 'flex',
		alignItems: 'center',
		gap: '1px',
		height: '100%',
	},
}));
